import assign from 'lodash-es/assign';
import { trace } from 'owa-trace';
import { getCorrelationVector } from 'owa-service/lib/correlationVector';
import { getClientVersion } from 'owa-config';
import { logUsage } from 'owa-analytics';
import { getConnectorsTokenAndLogTelemetry } from 'owa-actionable-message-connectors';

/**
 * The root string for teaching ui events
 */
const root: string = 'ActionableMessageTeachingUI';

/**
 * The prefix of the teaching ui events
 */
let prefix = `${root}`;

/**
 * The set of events triggered by teaching ui
 */
export const EVENTS = {
    CLICK: `${prefix}_BtnClicked`,
    GOTITCUSTOM: `${prefix}_GotItClicked`,
    LEARN: `${prefix}_LearnMoreClicked`,
    TRIGGERED: `${prefix}_Triggered`,
    DISMISSED: {
        BTNSCT: `${prefix}_DismisByAction`,
        UNMNT: `${prefix}_DismissByUnmount`,
    },
    SCOPE: {
        MOUNTED: `${prefix}_Mounted`,
        UNMNTED: `${prefix}_Unmounted`,
    },
    DISABLED: `${prefix}_FlightDisabled`,
    RENDERED: `${prefix}_Rendered`,
};

/**
 * Returns the uri associated with the card type
 */
//TODO: Move this into a more stable, centric location. Like a config file
export const URI: { [key: string]: string } = {
    Transaction:
        'https://support.office.com/en-us/article/pay-bills-and-invoices-in-outlook-using-microsoft-pay-1196e7e6-c096-44f1-a18a-9df3af832a48',
};

// The current TeachingUICorrelationValues object
let currCVS: TeachingUICorrelationValues | undefined;

/**
 * A type containing all of our correlation values
 *
 * @property {string} correlationVector the correlation vector
 * @property {string} adaptiveCardCorrelationId the correlation id of the adaptive card
 * @property {string} owaVersion the version of the owaVersion
 * @property {string} messageId the id of the message
 * @property {string} lightningAction the lightning action to take against a teaching ui module
 * @property {string} tuiType the type of teaching ui being fired, as well as the type of action being fired
 */
export type TeachingUICorrelationValues = {
    correlationVector: string;
    adaptiveCardCorrelationId: string;
    owaVersion: string;
    messageId: string;
    lightningAction: string;
    tuiType: string;
};

/**
 * Sets currCVS with the with new CVS
 *
 * @param {Partial<TeachingUICorrelationValues>} newCvs the new set of correlation values to be set
 */
function setCorrelationValues(newCvs: Partial<TeachingUICorrelationValues>): void {
    currCVS = currCVS || {
        correlationVector: '',
        adaptiveCardCorrelationId: '',
        owaVersion: '',
        messageId: '',
        lightningAction: '',
        tuiType: '',
    };

    //OPTIMIZE: Can be made a bit faster...
    assign(currCVS, newCvs);
}

/**
 * logs a teaching ui event
 *
 * @param {string} eventName the event name to log
 * @param {TeachingUICorrelationValues} adtlData teaching ui correlation vlaues to set
 */
export function logTeachingEvent(eventName: string, adtlData: TeachingUICorrelationValues): void {
    logUsage(eventName, adtlData);
    getConnectorsTokenAndLogTelemetry(
        {
            eventName: eventName,
            clientVersion: adtlData.owaVersion,
            clientType: 'WAC_Outlook_React',
            ...adtlData,
        },
        null
    );
}

/**
 * A collection of correlation vectors and lightning action types - will assit with logging
 *
 * @param {Partial<TeachingUICorrelationValues>} newCvs an optional set of correlation values we can set
 * @returns {TeachingUICorrelationValues} the set of correlation values logged
 */
export function teachingUITrace(
    newCvs?: Partial<TeachingUICorrelationValues>
): TeachingUICorrelationValues {
    if (newCvs) {
        try {
            trace.info('[ActionableMessageTeachingUI] Corelation telemetry trace function called');
            setCorrelationValues(newCvs);
        } catch (e) {
            trace.warn(
                '[ActionableMessageTeachingUI] Failed complete the logging of the telemetry - ' +
                    e.message
            );
        }
    }

    if (currCVS) {
        // Only call getCorrelationVector iff currCVS is set
        currCVS.correlationVector = currCVS ? getCorrelationVector() : undefined;
    }

    return currCVS;
}

/**
 * Initilizes the teaching ui trace values
 *
 * @param {{string, string, string}} newCvs the new set of correlation values to set
 */
export function initilizeTeachingUITrace(newCvs: {
    adaptiveCardCorrelationId: string;
    messageId: string;
    tuiType: string;
}): void {
    prefix = `${root}_`;
    teachingUITrace({
        adaptiveCardCorrelationId: newCvs.adaptiveCardCorrelationId,
        messageId: newCvs.messageId,
        owaVersion: getClientVersion(),
        lightningAction: '',
        tuiType: newCvs.tuiType,
    });
}

/**
 * helper function which merges the keys given into a string prefixed with 'ac'
 *
 * @param {string[]} keys the array of keys to join
 * @returns {string} a string prefixed with 'ac'
 */
